import React, { useEffect, useState } from 'react';
import { Modal } from 'react-bootstrap';
import { MdPassword } from 'react-icons/md';
import { ButtonCancel, ButtonSave, CardFooterCustom, CardIcon } from '../../styles';
import {
    BodyModal,
    ButtonSendCode,
    DescriptionModal,
    InputCode,
    MessageError,
    MessageSuccess,
    SendCodeMessage,
    TitleModal
} from './styles';
import { checkValidToken, CheckValidTokenResult, resendSignatureEmail } from '../../../../services/signature';
import Loading from '../../../../Components/Loading';
import formatTimeRemaining from '../../../../utils/formatTimeRemaining';
import { formatTimeHourMinMS } from '../../../../utils/formatDateMS';
import { maskEmail } from '../../../../utils/maskEmail';
import { cleanupBodyScrollLock } from '../../../../utils/modalCleanup';


type Props = {
    show: boolean;
    setShow: (show: boolean) => void;
    handleSave: (code: string) => void;
    email?: string;
    sendEmail?: () => void;
    hasValidCode: boolean;
    setHasValidCode: (value: boolean) => void;
    invalidCodeError?: boolean;
    setInvalidCodeError?: (value: boolean) => void;
};

const ModalCodeValidate = ({ show, setShow, handleSave, email, sendEmail, hasValidCode, setHasValidCode, invalidCodeError: externalInvalidCodeError, setInvalidCodeError: setExternalInvalidCodeError }: Props): JSX.Element => {
    const [code, setCode] = useState<string>('');
    const [errorInput, setErrorInput] = useState<boolean>(false);
    const [successInput, setSuccessInput] = useState<boolean>(false);
    const [tokenStatus, setTokenStatus] = useState<CheckValidTokenResult | null>(null);
    const [checkingToken, setCheckingToken] = useState<boolean>(true);
    const [invalidCodeError, setInvalidCodeError] = useState<boolean>(false);
    const [resentMessage, setResentMessage] = useState<boolean>(false);

    const handleClose = () => {
        setShow(false);
        cleanupBodyScrollLock();
    };

    const handleAssignCode = (e) => {
        const value: string = e.target.value.replace(/\D/g, '');
        setCode(value);
        setInvalidCodeError(false);
        setExternalInvalidCodeError?.(false);
        if (value.length !== 6) {
            setErrorInput(true);
            setSuccessInput(false);
        } else {
            setErrorInput(false);
            setSuccessInput(true);
            setHasValidCode(false);
        }
    }

    const handleCheckTokenAndSendEmail = async () => {
        const dataStorage = localStorage.getItem('gov_user_data');
        if (!dataStorage) {
            setTokenStatus({ hasValidToken: false, message: 'Dados do usuário não encontrados.' });
            return;
        }

        const userData = JSON.parse(dataStorage);
        const token: string | null = localStorage.getItem('gov_access_token');

        setResentMessage(false);

        const tokenValidation = await checkValidToken(userData.cpf, userData.contato.email, token);
        setTokenStatus(tokenValidation);

        if (!tokenValidation.hasValidToken && sendEmail) {
            await sendEmail();
        }
    }

    const handleCheckTokenAndResendEmail = async () => {
        const dataStorage = localStorage.getItem('gov_user_data');
        if (!dataStorage) {
            return;
        }

        const userData = JSON.parse(dataStorage);
        const token: string | null = localStorage.getItem('gov_access_token');

        const tokenValidation = await checkValidToken(userData.cpf, userData.contato.email, token);
        setTokenStatus(tokenValidation);

        if (tokenValidation.hasValidToken) {
            await resendSignatureEmail(userData.cpf, userData.contato.email, token);
            setResentMessage(true);
            return;
        }

        setResentMessage(false);
    }

    useEffect(() => {
        if (!show) {
            cleanupBodyScrollLock();
            return;
        }

        const checkTokenOnOpen = async () => {
            setCheckingToken(true);
            setResentMessage(false);
            try {
                await handleCheckTokenAndSendEmail();
            } catch (err) {
                console.error(err);
                setTokenStatus({ hasValidToken: false, message: 'Erro ao verificar token de assinatura.' });
            } finally {
                setCheckingToken(false);
            }
        };

        checkTokenOnOpen();
    }, [show]);

    return (
        <>
            <Modal
                size='lg'
                show={show}
                centered
                onHide={handleClose}
                onExited={cleanupBodyScrollLock}
                backdrop
                keyboard
            >
                <Modal.Body>
                    <BodyModal>
                        <CardIcon>
                            <MdPassword />
                        </CardIcon>
                        <TitleModal>Assinatura eletrônica</TitleModal>
                        {checkingToken ? (
                            <Loading />
                        ) : (
                            <>
                                {tokenStatus && tokenStatus.hasValidToken ? (
                                    <DescriptionModal>
                                        {resentMessage ? (
                                            <>Um e-mail com seu token atual foi enviado para <strong>{maskEmail(email ?? '')}</strong>. Seu token tem validade de <strong>
                                                {tokenStatus.expiresAt && formatTimeRemaining(tokenStatus.expiresAt)}
                                            </strong></>
                                        ) : (
                                            <>Você possui token válido neste momento. Use o token atual enviado anteriormente para o seu e-mail <strong>{maskEmail(email ?? '')}</strong>. Seu token tem validade de <strong>
                                                {tokenStatus.expiresAt && formatTimeRemaining(tokenStatus.expiresAt)}
                                            </strong></>
                                        )}
                                    </DescriptionModal>
                                ) : (
                                    <DescriptionModal>
                                        {tokenStatus?.expiresAt ? (
                                            <>Seu token expirou às <strong>{formatTimeHourMinMS(tokenStatus.expiresAt)}</strong>. Um novo token válido foi enviado para o seu e-mail: <strong>{maskEmail(email ?? '')}</strong></>
                                        ) : (
                                            <>Seu token expirou. Um novo token válido foi enviado para o seu e-mail: <strong>{maskEmail(email ?? '')}</strong></>
                                        )}
                                    </DescriptionModal>
                                )}

                                <InputCode placeholder='000000' maxLength={6} value={code}
                                    onChange={handleAssignCode}
                                    error={(errorInput || hasValidCode || invalidCodeError || externalInvalidCodeError)}
                                    success={(successInput && !hasValidCode && !invalidCodeError && !externalInvalidCodeError)}
                                />
                                {errorInput && !hasValidCode && !invalidCodeError && !externalInvalidCodeError && <MessageError>Código expirado ou inexistente</MessageError>}
                                {(hasValidCode || invalidCodeError || externalInvalidCodeError) && <MessageError>Código expirado ou inexistente</MessageError>}

                                {!hasValidCode && !invalidCodeError && !externalInvalidCodeError && successInput && <MessageSuccess>Verificado</MessageSuccess>}
                                <SendCodeMessage>Não recebeu o código? <ButtonSendCode type="button" onClick={handleCheckTokenAndResendEmail}>Reenviar</ButtonSendCode></SendCodeMessage>
                            </>
                        )}


                    </BodyModal>
                </Modal.Body>
                <CardFooterCustom>
                    <ButtonCancel type="button" onClick={handleClose}>
                        Cancelar
                    </ButtonCancel>
                    <ButtonSave type="button" onClick={() => handleSave(code)} disabled={!successInput}>
                        Finalizar
                    </ButtonSave>
                </CardFooterCustom>
            </Modal>
        </>
    );
};

export default ModalCodeValidate;
